import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { fetchOwnerWallets, resolveMainWalletForCurrency } from '@/store/slices/wallet';

import type { MailboxPrice } from '../../mock-data/mailbox-records';
import { MAILBOX_LEDGER_CURRENCY, WALLET_OVERRIDE_ACTIVE } from '../../mock-data/mailbox-wallet';
import { formatAmountWithCode } from '../../utils/money';
import { addMailboxWalletSpent } from './store';
import { createMailboxInvoice, settleMailboxFromWallet, settleMailboxByCard, cancelMailboxInvoice } from './purchase';

/** The two ways to settle a mailbox invoice, the same pair the product payment chooser offers. */
export type MailboxPaymentMethodId = 'wallet' | 'card';

export type UsePurchase = {
  /** The MAIN wallet's available balance in the price currency, or null until loaded / unmatched, and
   *  null while the price (and so its currency) is not known. */
  balance: number | null;
  /** The balance in the wallet's OWN currency ("1,000.00 USD"), or null until the wallets have loaded. */
  balanceLabel: string | null;
  /** Mint the invoice for one mailbox (see `purchase.ts`). Called on Pay, never on
   *  submit. */
  mint: () => Promise<{ invoiceId: string }>;
  /**
   * Settle a minted invoice by the chosen method. A wallet settlement moves the balance (the
   * stand-in ledger while the override is active); a card settlement leaves it alone. Rejects when
   * the price is not known: nothing is charged at a price nobody has read.
   */
  settle: (invoiceId: string, method: MailboxPaymentMethodId) => Promise<void>;
  /** Abandon an unpaid invoice (see `purchase.ts`). The balance is untouched. */
  cancel: (invoiceId: string) => Promise<void>;
};

/**
 * Paying for a mailbox beyond the included one: the platform's own payment chooser (wallet by
 * default, card when the balance is short), at the price the service answered (`price`, `null`
 * until it has). Nothing here knows a price of its own.
 *
 * REAL READS: the balance and its currency come from the wallet slice (`fetchOwnerWallets`, loaded
 * once per mount when not already loaded, then `resolveMainWalletForCurrency` for the price's
 * currency). ONLY the calls in `purchase.ts` (mint / settle / cancel) stand in for a backend that
 * does not exist yet; swapping that one file is the whole switch-over.
 *
 * DEV-ONLY LEDGER: the balance is the real one, but no backend can charge it for a mailbox yet, so
 * outside production each wallet settlement is added to Scenario C's OWN per-brand local spend
 * (`store.ts`, persisted under its own key). The READ happens in `resolveMainWalletForCurrency`
 * itself, which subtracts the total across every scenario's ledger, so this hook, the top bar, the
 * Wallet page, the product sheets and both mailbox sections all show one figure; nothing is
 * subtracted here a second time. The ledger only ever debits a balance in its own currency
 * (`MAILBOX_LEDGER_CURRENCY`), so a price in another currency is not written to it. After a wallet
 * settlement the wallets are refetched so the always-mounted header re-runs the resolver at once.
 * This whole branch disappears when the real wallet is wired: the slice's balance then moves on its
 * own once `payInvoiceFromWallet` settles a real mailbox invoice.
 *
 * Deliberately Scenario C's own hook, the same job as Scenario B's and no import between them.
 */
export const usePurchase = (brandId: string | undefined, price: MailboxPrice | null): UsePurchase => {
  const dispatch = useAppDispatch();
  const ownerWallet = useAppSelector((s) => s.ownerWallet ?? null);
  const walletLoaded = useAppSelector((s) => s.ownerWallet?.loaded ?? false);

  // The balance needs the owner's wallets; load them once on mount if nothing has yet.
  React.useEffect(() => {
    if (!walletLoaded) void dispatch(fetchOwnerWallets());
  }, [walletLoaded, dispatch]);

  const ledgerActive = WALLET_OVERRIDE_ACTIVE && brandId !== undefined;

  // Derived in render: `ownerWallet` is a stable reference, so no per-dispatch churn. The resolver
  // already returns the ledger-debited figure; subtracting here again would double-count. Without a
  // price there is no currency to match, so the balance reads unmatched (null below).
  const view = resolveMainWalletForCurrency(ownerWallet, price?.currency ?? null);
  const balance = view.balance;
  const matched = price !== null && view.matched;
  const currency = view.currency;
  // Labelled in the wallet's OWN currency: when no MAIN wallet in the price's currency exists the
  // slice falls back to the primary wallet, and its balance must not be captioned as the price's.
  const balanceLabel = balance === null ? null : formatAmountWithCode(balance, currency, 2);

  const mint = React.useCallback(() => createMailboxInvoice(), []);

  const settle = React.useCallback(
    async (invoiceId: string, method: MailboxPaymentMethodId): Promise<void> => {
      if (price === null) throw new Error('The price is not known, so nothing can be charged.');
      if (method === 'card') {
        await settleMailboxByCard(invoiceId);
        return;
      }
      await settleMailboxFromWallet(invoiceId);
      // Only the ledger moves here: the real wallet slice re-reads its own balance once a real
      // `payInvoiceFromWallet` settles a mailbox invoice. It converts to cents itself (store.ts).
      if (ledgerActive && brandId && price.currency === MAILBOX_LEDGER_CURRENCY) {
        // The ledger is read from and written to storage in one place (store.ts), so nothing here
        // can go stale across a brand switch.
        addMailboxWalletSpent(brandId, price.amount);
        // The header holds the same `ownerWallet` reference and would not re-render from this
        // hook's state alone; a refetch (the server returns the same number, which is expected)
        // makes every subscriber re-run the resolver against the ledgers.
        void dispatch(fetchOwnerWallets());
      }
    },
    [ledgerActive, brandId, dispatch, price],
  );

  const cancel = React.useCallback((invoiceId: string) => cancelMailboxInvoice(invoiceId), []);

  return {
    balance: matched ? balance : null,
    balanceLabel,
    mint,
    settle,
    cancel,
  };
};

export default usePurchase;
